import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { ENDPOINTS } from '../config/endpoints';

interface SessionData {
  token: string;
  networkId: string;
  networkName: string;
  expiresAt: string;
  networkConfig: {
    requires_fca: boolean;
    allow_bulk_upload: boolean;
    max_bulk_upload: number;
    branding: {
      primary_color: string;
      logo_url?: string;
    };
  };
}

export const AdminPortal: React.FC = () => {
  const navigate = useNavigate();
  const [pin, setPin] = useState<string>('');
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [sessionData, setSessionData] = useState<SessionData | null>(null);

  // Check for existing valid session on mount
  useEffect(() => {
    const storedSession = localStorage.getItem('admin_session');
    if (storedSession) {
      try {
        const session: SessionData = JSON.parse(storedSession);
        const expiresAt = new Date(session.expiresAt);

        // Check if session is still valid
        if (expiresAt > new Date()) {
          setSessionData(session);
          // Redirect to network onboarding form
          navigate(`/admin/${session.networkId}`);
        } else {
          // Session expired, clear it
          localStorage.removeItem('admin_session');
        }
      } catch (e) {
        // Invalid session data, clear it
        localStorage.removeItem('admin_session');
      }
    }
  }, [navigate]);

  const handlePinChange = (value: string) => {
    setPin(value.toUpperCase());
    setError('');
  };

  const validatePin = async () => {
    if (!pin.trim()) {
      setError('Please enter your PIN');
      return;
    }

    setIsValidating(true);
    setError('');

    // Extract network ID from PIN format (e.g., "JM-2025-X7K9" -> "just-mortgages")
    const pinPrefix = pin.trim().split('-')[0].toUpperCase();
    let networkId = '';

    if (pinPrefix === 'JM') {
      networkId = 'just-mortgages';
    } else if (pinPrefix === 'LG') {
      networkId = 'lg';
    } else if (pinPrefix === 'SESAME') {
      networkId = 'sesame';
    } else {
      setError('Invalid PIN format');
      setIsValidating(false);
      return;
    }

    try {
      const response = await fetch(ENDPOINTS.VALIDATE_NETWORK_PIN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          networkId: networkId,
          pin: pin.trim(),
        }),
      });

      const data = await response.json();

      if (response.ok && data.valid) {
        // Store session data
        const session: SessionData = {
          token: data.token,
          networkId: data.network_id,
          networkName: data.network_name,
          expiresAt: data.expires_at,
          networkConfig: data.network_config,
        };

        localStorage.setItem('admin_session', JSON.stringify(session));
        setSessionData(session);

        // Redirect to network onboarding form
        navigate(`/admin/${data.network_id}`);
      } else {
        setError(data.error || 'Invalid PIN or network. Please try again.');
      }
    } catch (err) {
      console.error('PIN validation error:', err);
      setError('Unable to validate PIN. Please check your connection and try again.');
    } finally {
      setIsValidating(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    validatePin();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Network Admin Portal
          </h1>
          <p className="text-gray-600">
            Sign in with your network PIN to onboard member firms
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* PIN Entry */}
          <div>
            <label htmlFor="pin" className="block text-sm font-medium text-gray-700 mb-2">
              Network Admin PIN
            </label>
            <input
              type="text"
              id="pin"
              name="pin"
              value={pin}
              onChange={(e) => handlePinChange(e.target.value)}
              placeholder="Enter your PIN (e.g., JM-2025-XXXXXXXX)"
              className={classNames(
                'w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 font-mono text-center text-lg tracking-wider',
                {
                  'border-red-300 focus:border-red-500 focus:ring-red-500': error,
                  'border-gray-300 focus:border-blue-500 focus:ring-blue-500': !error,
                }
              )}
              disabled={isValidating}
              autoComplete="off"
            />
            <p className="mt-2 text-xs text-gray-500 text-center">
              Enter the PIN provided by your network administrator
            </p>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-red-600">{error}</p>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isValidating || !pin.trim()}
            className={classNames(
              'w-full py-3 px-4 rounded-lg font-medium transition-all duration-200 flex items-center justify-center space-x-2',
              {
                'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl': !isValidating && pin.trim(),
                'bg-gray-300 text-gray-500 cursor-not-allowed': isValidating || !pin.trim(),
              }
            )}
          >
            {isValidating ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Validating...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
                <span>Access Admin Portal</span>
              </>
            )}
          </button>
        </form>

        {/* Help Text */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            Need help? Contact your network administrator for PIN access.
          </p>
        </div>

        {/* Security Notice */}
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-start space-x-2">
            <svg className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <p className="text-xs text-gray-600">
              This portal is for authorized network administrators only. Your session will expire after 24 hours.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
